import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  getBranches, 
  getProducts, 
  getBranchInventory, 
  createBranchInventory, 
  updateBranchInventory,
  createInventoryTransfer,
  getInventoryTransfers
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Package, 
  Plus, 
  Edit2, 
  Building2, 
  ArrowRightLeft, 
  AlertTriangle,
  Search,
  Filter
} from 'lucide-react';

const BranchInventory = () => {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [addStockDialog, setAddStockDialog] = useState(false);
  const [editStockDialog, setEditStockDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  
  // Forms
  const [stockForm, setStockForm] = useState({
    product_id: '',
    branch_id: '',
    size: '',
    color: '',
    stock: 0
  });
  const [editingInventory, setEditingInventory] = useState(null);
  const [transferForm, setTransferForm] = useState({
    from_branch_id: '',
    to_branch_id: '',
    product_id: '',
    size: '',
    color: '',
    quantity: 0,
    notes: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedBranch || user?.role === 'admin') {
      loadInventory();
    }
  }, [selectedBranch]);

  const loadInitialData = async () => {
    try {
      const [branchesData, productsData, transfersData] = await Promise.all([
        getBranches(),
        getProducts(),
        user?.role === 'admin' ? getInventoryTransfers() : Promise.resolve([])
      ]);
      setBranches(branchesData);
      setProducts(productsData.products || productsData);
      setTransfers(transfersData);
      
      // Si el usuario tiene sucursal asignada, seleccionarla
      if (user?.branch_id) {
        setSelectedBranch(user.branch_id);
      } else if (branchesData.length > 0 && user?.role === 'admin') {
        setSelectedBranch(branchesData[0].id);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadInventory = async () => {
    try {
      const data = await getBranchInventory(selectedBranch);
      setInventory(data);
    } catch (error) {
      toast.error('Error al cargar inventario');
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    try {
      await createBranchInventory({
        ...stockForm,
        branch_id: stockForm.branch_id || selectedBranch
      });
      toast.success('Stock agregado');
      setAddStockDialog(false);
      resetStockForm();
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al agregar stock');
    }
  };

  const handleUpdateStock = async (e) => {
    e.preventDefault();
    try {
      await updateBranchInventory(editingInventory.id, { stock: editingInventory.stock });
      toast.success('Stock actualizado');
      setEditStockDialog(false);
      setEditingInventory(null);
      loadInventory();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar stock');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await createInventoryTransfer(transferForm);
      toast.success('Transferencia realizada');
      setTransferDialog(false);
      resetTransferForm();
      loadInventory();
      // Recargar transferencias
      const transfersData = await getInventoryTransfers();
      setTransfers(transfersData);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error en la transferencia');
    }
  };

  const resetStockForm = () => {
    setStockForm({
      product_id: '',
      branch_id: '',
      size: '',
      color: '',
      stock: 0
    });
  };

  const resetTransferForm = () => {
    setTransferForm({
      from_branch_id: '',
      to_branch_id: '',
      product_id: '',
      size: '',
      color: '',
      quantity: 0,
      notes: ''
    });
  };

  const openEditDialog = (inv) => {
    setEditingInventory({ ...inv });
    setEditStockDialog(true);
  };

  const getProductSizesColors = (productId) => {
    const product = products.find(p => p.id === productId);
    return {
      sizes: product?.sizes || [],
      colors: product?.colors || []
    };
  };

  // Filtrar inventario por búsqueda
  const filteredInventory = inventory.filter(inv => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      inv.product_name?.toLowerCase().includes(search) ||
      inv.size?.toLowerCase().includes(search) ||
      inv.color?.toLowerCase().includes(search)
    );
  });

  // Agrupar por producto para mejor visualización
  const groupedInventory = filteredInventory.reduce((acc, inv) => {
    const key = inv.product_id;
    if (!acc[key]) {
      acc[key] = {
        product_id: inv.product_id,
        product_name: inv.product_name,
        variants: []
      };
    }
    acc[key].variants.push(inv);
    return acc;
  }, {});

  const inventoryProducts = products.filter(p => p.product_type !== 'SERVICIO' && p.track_inventory !== false);
  const selectedProduct = inventoryProducts.find(p => p.id === stockForm.product_id);
  const transferProduct = inventoryProducts.find(p => p.id === transferForm.product_id);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="branch-inventory-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>
          Inventario por Sucursal
        </h1>
        <div className="flex gap-2">
          {user?.role === 'admin' && (
            <Button
              onClick={() => setTransferDialog(true)}
              variant="outline"
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
              data-testid="transfer-btn"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Transferir
            </Button>
          )}
          {user?.role === 'admin' && (
            <Button
              onClick={() => setAddStockDialog(true)}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="add-stock-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Stock
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="inventory" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="inventory">Inventario</TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="transfers">Transferencias</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="inventory">
          {/* Filtros */}
          <Card className="bg-white border border-slate-200 mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {user?.role === 'admin' && (
                  <div className="space-y-2">
                    <Label>Sucursal</Label>
                    <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                      <SelectTrigger data-testid="branch-select">
                        <SelectValue placeholder="Seleccionar sucursal" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2 md:col-span-2">
                  <Label>Buscar Producto</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar por nombre, talla o color..."
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Inventario */}
          {!selectedBranch ? (
            <Card className="bg-white border border-slate-200">
              <CardContent className="p-8 text-center">
                <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Selecciona una sucursal para ver el inventario</p>
              </CardContent>
            </Card>
          ) : Object.keys(groupedInventory).length === 0 ? (
            <Card className="bg-white border border-slate-200">
              <CardContent className="p-8 text-center">
                <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No hay inventario en esta sucursal</p>
                {user?.role === 'admin' && (
                  <Button
                    onClick={() => setAddStockDialog(true)}
                    className="mt-4 bg-cyan-500 hover:bg-cyan-600 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Primer Stock
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.values(groupedInventory).map((group) => (
                <Card key={group.product_id} className="bg-white border border-slate-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center text-lg" style={{ fontFamily: 'Outfit' }}>
                      <Package className="w-5 h-5 mr-2 text-cyan-500" />
                      {group.product_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 font-medium">Talla</th>
                            <th className="text-left p-3 font-medium">Color</th>
                            <th className="text-center p-3 font-medium">Stock</th>
                            <th className="text-center p-3 font-medium">Estado</th>
                            {user?.role === 'admin' && (
                              <th className="text-center p-3 font-medium">Acciones</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {group.variants.map((variant) => {
                            const product = products.find(p => p.id === variant.product_id);
                            const minStock = product?.min_stock || 5;
                            const isLowStock = variant.stock <= minStock;
                            
                            return (
                              <tr key={variant.id} className="border-b" data-testid={`inventory-row-${variant.id}`}>
                                <td className="p-3">{variant.size || '-'}</td>
                                <td className="p-3">
                                  <span className="flex items-center">
                                    {variant.color && (
                                      <span 
                                        className="w-4 h-4 rounded-full mr-2 border"
                                        style={{ backgroundColor: variant.color.toLowerCase() }}
                                      />
                                    )}
                                    {variant.color || '-'}
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className={`font-bold ${isLowStock ? 'text-red-600' : 'text-green-600'}`}>
                                    {variant.stock}
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  {isLowStock ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      Bajo Stock
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                                      OK
                                    </span>
                                  )}
                                </td>
                                {user?.role === 'admin' && (
                                  <td className="p-3 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openEditDialog(variant)}
                                      data-testid={`edit-stock-${variant.id}`}
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {user?.role === 'admin' && (
          <TabsContent value="transfers">
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit' }}>
                  Historial de Transferencias
                </CardTitle>
              </CardHeader>
              <CardContent>
                {transfers.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    No hay transferencias registradas
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 font-medium">Fecha</th>
                          <th className="text-left p-3 font-medium">Producto</th>
                          <th className="text-left p-3 font-medium">Variante</th>
                          <th className="text-left p-3 font-medium">Origen</th>
                          <th className="text-left p-3 font-medium">Destino</th>
                          <th className="text-center p-3 font-medium">Cantidad</th>
                          <th className="text-left p-3 font-medium">Usuario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transfers.map((transfer) => (
                          <tr key={transfer.id} className="border-b">
                            <td className="p-3 text-xs">
                              {new Date(transfer.created_at).toLocaleDateString('es-DO')}
                            </td>
                            <td className="p-3">{transfer.product_name}</td>
                            <td className="p-3">{transfer.size} / {transfer.color}</td>
                            <td className="p-3">{transfer.from_branch_name}</td>
                            <td className="p-3">{transfer.to_branch_name}</td>
                            <td className="p-3 text-center font-bold">{transfer.quantity}</td>
                            <td className="p-3 text-xs">{transfer.created_by_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog: Agregar Stock */}
      <Dialog open={addStockDialog} onOpenChange={setAddStockDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Agregar Stock</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStock} className="space-y-4">
            <div className="space-y-2">
              <Label>Sucursal *</Label>
              <Select 
                value={stockForm.branch_id || selectedBranch} 
                onValueChange={(value) => setStockForm({ ...stockForm, branch_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Producto *</Label>
              <Select 
                value={stockForm.product_id} 
                onValueChange={(value) => setStockForm({ ...stockForm, product_id: value, size: '', color: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar producto" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedProduct && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Talla</Label>
                    <Select 
                      value={stockForm.size || "none"} 
                      onValueChange={(value) => setStockForm({ ...stockForm, size: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin talla</SelectItem>
                        {selectedProduct.sizes?.map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <Select 
                      value={stockForm.color || "none"} 
                      onValueChange={(value) => setStockForm({ ...stockForm, color: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin color</SelectItem>
                        {selectedProduct.colors?.map((color) => (
                          <SelectItem key={color} value={color}>{color}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Cantidad Inicial *</Label>
              <Input
                type="number"
                min="0"
                value={stockForm.stock}
                onChange={(e) => setStockForm({ ...stockForm, stock: parseInt(e.target.value) || 0 })}
                onFocus={(e) => e.target.select()}
                data-testid="stock-input"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setAddStockDialog(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white">
                Agregar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Editar Stock */}
      <Dialog open={editStockDialog} onOpenChange={setEditStockDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Editar Stock</DialogTitle>
          </DialogHeader>
          {editingInventory && (
            <form onSubmit={handleUpdateStock} className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <p><strong>Producto:</strong> {editingInventory.product_name}</p>
                <p><strong>Talla:</strong> {editingInventory.size || '-'}</p>
                <p><strong>Color:</strong> {editingInventory.color || '-'}</p>
                <p><strong>Sucursal:</strong> {editingInventory.branch_name}</p>
              </div>

              <div className="space-y-2">
                <Label>Nuevo Stock</Label>
                <Input
                  type="number"
                  min="0"
                  value={editingInventory.stock}
                  onChange={(e) => setEditingInventory({
                    ...editingInventory,
                    stock: parseInt(e.target.value) || 0
                  })}
                  onFocus={(e) => e.target.select()}
                  data-testid="edit-stock-input"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditStockDialog(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white">
                  Guardar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Transferencia */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Transferir Inventario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransfer} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sucursal Origen *</Label>
                <Select 
                  value={transferForm.from_branch_id} 
                  onValueChange={(value) => setTransferForm({ ...transferForm, from_branch_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sucursal Destino *</Label>
                <Select 
                  value={transferForm.to_branch_id} 
                  onValueChange={(value) => setTransferForm({ ...transferForm, to_branch_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.filter(b => b.id !== transferForm.from_branch_id).map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Producto *</Label>
              <Select 
                value={transferForm.product_id} 
                onValueChange={(value) => setTransferForm({ ...transferForm, product_id: value, size: '', color: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar producto" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {transferProduct && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Talla</Label>
                  <Select 
                    value={transferForm.size || "none"} 
                    onValueChange={(value) => setTransferForm({ ...transferForm, size: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin talla</SelectItem>
                      {transferProduct.sizes?.map((size) => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <Select 
                    value={transferForm.color || "none"} 
                    onValueChange={(value) => setTransferForm({ ...transferForm, color: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin color</SelectItem>
                      {transferProduct.colors?.map((color) => (
                        <SelectItem key={color} value={color}>{color}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Cantidad a Transferir *</Label>
              <Input
                type="number"
                min="1"
                value={transferForm.quantity}
                onChange={(e) => setTransferForm({ ...transferForm, quantity: parseInt(e.target.value) || 0 })}
              />
            </div>

            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Input
                value={transferForm.notes}
                onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                placeholder="Motivo de la transferencia..."
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setTransferDialog(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white">
                Transferir
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchInventory;